import type { OnDestroy } from '@angular/core';
import { Directive, inject, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';

import type { Uuid } from '@common/types';

import { ESelectedEntityType } from '@common/types/layout.type';
import { LayoutFacade } from '@web/store/layout/layout.facade';

@Directive()
export abstract class AbstractBaseSignalSettingsComponent implements OnDestroy {
    protected readonly translate: TranslateService = inject(TranslateService);
    protected readonly layoutFacade: LayoutFacade = inject(LayoutFacade);

    protected unsubscribe$ = new Subject<void>();

    /**
     * The two "edit the four of them separately" switches — the contour's sides
     * and the radius' corners — per breakpoint.
     *
     * Neither is stored on the entity: `isBorderIndependent` and
     * `isBorderRadiusIndependent` derive them from the sides and the corners,
     * which is what those already say. But a switch has to survive being turned
     * *on*: at that moment the four are still equal, so a fresh derivation would
     * read `false` and flip it straight back. So the panel keeps the user's
     * answer for as long as it is open, and a missing entry means "nobody has
     * touched it — read the values".
     *
     * Per breakpoint because the values are, and per panel instance because the
     * settings panel is rebuilt whenever the selection moves (`selectionKey$`),
     * which is exactly when this should be forgotten.
     */
    private readonly independentToggles = signal<Partial<Record<string, boolean>>>({});

    private resolveIndependent(key: string, derived: boolean): boolean {
        return this.independentToggles()[key] ?? derived;
    }

    private rememberIndependent(key: string, value: boolean): void {
        this.independentToggles.update((state) => ({ ...state, [key]: value }));
    }

    protected resolveIndependentBorders(device: string, derived: boolean): boolean {
        return this.resolveIndependent(`borders:${device}`, derived);
    }

    protected rememberIndependentBorders(device: string, value: boolean): void {
        this.rememberIndependent(`borders:${device}`, value);
    }

    protected resolveIndependentCorners(device: string, derived: boolean): boolean {
        return this.resolveIndependent(`corners:${device}`, derived);
    }

    protected rememberIndependentCorners(device: string, value: boolean): void {
        this.rememberIndependent(`corners:${device}`, value);
    }

    public setSelectedTheme(): void {
        this.layoutFacade.setSelectedEntity({ entityType: ESelectedEntityType.theme });
    }

    public setSelectedBlock(blockId: Uuid): void {
        this.layoutFacade.setSelectedEntity({ entityType: ESelectedEntityType.block, entityId: blockId });
    }

    public setSelectedSpecificBlock(blockId: Uuid): void {
        this.layoutFacade.setSelectedEntity({ entityType: ESelectedEntityType.specificBlock, entityId: blockId });
    }

    public setSelectedSpecificSection(sectionId: Uuid): void {
        this.layoutFacade.setSelectedEntity({ entityType: ESelectedEntityType.specificSection, entityId: sectionId });
    }

    public setSelectedColumn(columnId: Uuid): void {
        this.layoutFacade.setSelectedEntity({ entityType: ESelectedEntityType.column, entityId: columnId });
    }

    public setSelectedGrid(gridId: Uuid): void {
        this.layoutFacade.setSelectedEntity({ entityType: ESelectedEntityType.grid, entityId: gridId });
    }

    public ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }
}
